import { capitalize } from 'lodash';
import {
	ExposureConfigurationResponse,
	PerilConfigurationResponse,
	ProductConfigurationResponse,
} from '../../api/ConfigApi';
import { ConfigData } from '../../context/config';
import {
	ADD_PAYABLE_MODAL_AMOUNT_LESS_THAN_AVAILABLE_RESERVE_HELPER_TEXT,
	ADD_PAYABLE_MODAL_AMOUNT_MAY_NOT_BE_NEGATIVE_HELPER_TEXT,
	ADD_PAYABLE_MODAL_AMOUNT_MUST_BE_VALID_NUMBER_HELPER_TEXT,
} from '../../data/labelsAndMessages';
import { SubClaimResponse, SubClaimSummary } from '../../types/claim';
import { CurrencyCode } from '../../types/currencyCode';
import { Exposure, Peril, Policy2 } from '../../types/policy';
import { formatCurrency, isPerilActive } from '../../util/util';

export function checkInvalidReserveValue(reserve: string) {
	if (!reserve) {
		return true;
	}
	return parseFloat(reserve) < 0;
}

export function getReserveError(reserve: string) {
	if (!reserve) {
		return 'Reserve must be valid number';
	}
	if (parseFloat(reserve) < 0) {
		return 'Reserve amounts may not be negative.';
	}
	return null;
}

export function formatLedgerType(changeType: string) {
	if (changeType === 'payable.created') {
		return 'Payable Added';
	} else if (changeType === 'payable.reversed') {
		return 'Payable Reversed';
	} else if (changeType === 'reserve.closed') {
		return 'Subclaim Closed';
	} else if (changeType === 'reserve.opened') {
		return 'Subclaim Created';
	} else if (changeType === 'reserve.reopened') {
		return 'Subclaim Reopened';
	} else if (changeType === 'reserve.updated') {
		return 'Reserve Updated';
	}
	return null;
}

export function formatPayableType(payableType: string) {
	return capitalize(payableType);
}

export function formatReserve(reserve: string, withMathAbs?: boolean) {
	if (!reserve) return null;
	if (withMathAbs) return Math.abs(parseFloat(reserve)).toFixed(2);

	return parseFloat(reserve).toFixed(2);
}

export function formatRequestedReserve(reserve: string) {
	if (!reserve) return null;

	return (-Math.abs(parseFloat(reserve))).toFixed(2);
}

export function formatRequestedPayable(amount: string) {
	if (!amount) return null;

	return parseFloat(formatRequestedReserve(amount));
}

export function checkInvalidPayableValue(
	amount: string,
	availableReserve: string,
) {
	if (!amount) return true;
	if (!availableReserve) return true;
	if (parseFloat(amount) < 0) return true;

	return parseFloat(amount) > parseFloat(availableReserve);
}

export function getPayableError(amount: string, availableReserve: string) {
	if (!amount) {
		return ADD_PAYABLE_MODAL_AMOUNT_MUST_BE_VALID_NUMBER_HELPER_TEXT();
	}

	if (parseFloat(amount) < 0) {
		return ADD_PAYABLE_MODAL_AMOUNT_MAY_NOT_BE_NEGATIVE_HELPER_TEXT();
	}

	if (parseFloat(amount) > parseFloat(availableReserve)) {
		return ADD_PAYABLE_MODAL_AMOUNT_LESS_THAN_AVAILABLE_RESERVE_HELPER_TEXT();
	}

	return null;
}

export function calculateTotalReserveAmount(
	total: number,
	payable: number,
	reserve: number,
) {
	return (
		total +
		(Math.abs(parseFloat(payable.toString())) +
			Math.abs(parseFloat(reserve.toString())))
	);
}

export function getTotalClaimReserves(subclaimSummaries: SubClaimSummary[]) {
	let totalLossReserve = 0;
	let totalExpenseReserve = 0;
	subclaimSummaries.forEach((summary) => {
		totalLossReserve = calculateTotalReserveAmount(
			totalLossReserve,
			summary.lossPayableAmount,
			summary.lossReserveAmount,
		);
		totalExpenseReserve = calculateTotalReserveAmount(
			totalExpenseReserve,
			summary.expensePayableAmount,
			summary.expenseReserveAmount,
		);
	});
	return { totalLossReserve, totalExpenseReserve };
}

export const formatPayableAmount = (
	amount: number,
	currency: CurrencyCode,
	isReversal: boolean,
) => {
	const formattedAmount = formatCurrency(Math.abs(amount), currency);
	return isReversal ? `-${formattedAmount}` : formattedAmount;
};

export const getSubclaimDisplayName = (
	subClaim: SubClaimResponse,
	policy: Policy2,
	configData: ConfigData,
) => {
	const exposureLocator = subClaim.exposureLocator;
	const perilLocator = subClaim.perilLocator;
	const exposure = policy.exposures.find(
		(element: Exposure) => element.locator == exposureLocator,
	);
	if (typeof exposure == undefined) {
		return '';
	}
	if (typeof exposure.perils == undefined) {
		return '';
	}
	const peril = exposure.perils.find(
		(element: Peril) => element.locator == perilLocator,
	);
	if (typeof peril == undefined) {
		return '';
	}

	const productName = policy.productName;
	const displayCat = configData.productConfigs.find(
		(element: ProductConfigurationResponse) => element.name == productName,
	);
	if (typeof displayCat == undefined) {
		return '';
	}
	const policyExposure = policy.exposures.find(
		(element: Exposure) => element.locator == exposureLocator,
	);
	if (typeof policyExposure == undefined) {
		return '';
	}
	const exposureForPeril = displayCat.policyConfiguration.exposures.find(
		(element: ExposureConfigurationResponse) =>
			element.name == policyExposure.name,
	);

	if (typeof exposureForPeril == undefined) {
		return '';
	}
	if (typeof exposureForPeril.perils == undefined) {
		return '';
	}
	const displayName = exposureForPeril.perils.find(
		(element: PerilConfigurationResponse) => element.name == peril.name,
	).displayName;
	if (typeof displayName == undefined) {
		return '';
	}
	return displayName;
};

export function checkForInvalidatedSubclaims(
	incidentTimestamp: string,
	subClaims: SubClaimResponse[],
	policy: Policy2,
	configData: ConfigData,
) {
	let invalidSubclaims = [];
	if (subClaims && subClaims?.length) {
		invalidSubclaims = subClaims.reduce(function (filtered, subclaim) {
			const exposureLocator = subclaim.exposureLocator;
			const perilLocator = subclaim.perilLocator;
			const exposureObj = policy?.exposures?.find(
				(e) => e.locator === exposureLocator,
			);
			if (!exposureObj) {
				return filtered;
			} // if no match, don't continue
			const perilObj =
				exposureObj &&
				exposureObj.perils.find((p) => p.locator === perilLocator);
			if (!perilObj) {
				return filtered;
			} // if no match, don't continue
			// check that peril has coverage on incident date
			const subclaimPerilInactive = !isPerilActive(
				perilObj,
				exposureObj,
				incidentTimestamp,
			);

			if (subclaimPerilInactive) {
				const perilDisplayName = getSubclaimDisplayName(
					subclaim,
					policy,
					configData,
				);
				const lossReserve = parseFloat(subclaim.lossReserve.currentAmount);
				const expenseReserve = parseFloat(
					subclaim.expenseReserve.currentAmount,
				);
				const totalLossReserve = formatCurrency(
					Math.abs(lossReserve),
					subclaim.reserveCurrency as CurrencyCode,
				);
				const totalExpenseReserve = formatCurrency(
					Math.abs(expenseReserve),
					subclaim.reserveCurrency as CurrencyCode,
				);
				const subclaimStatus = capitalize(subclaim.currentStatus);
				filtered.push({
					locator: subclaim.locator,
					perilDisplayName,
					totalLossReserve,
					totalExpenseReserve,
					subclaimStatus,
				});
			}
			return filtered;
		}, []);
	}
	return invalidSubclaims;
}
